/**
 * Which tracks a cycle-level guided path writes to, and what it writes there.
 *
 * Criteria (and their weights), value ranges and people are track records (canon 28 §2.2, §5.1): a
 * cycle has none of its own. So the cycle configure wizard writes each answer to
 * the cycle's open tracks, one track at a time, through the same store calls the
 * track page makes. A track whose content is locked is shown as locked and left
 * alone (the server refuses the write anyway, migrations 1006 and 1031); so is a
 * track the person may not change.
 */
import type { Track } from '../types/prioritization/track';

export type WizardTrackContent = 'criteria' | 'weights' | 'ranges' | 'people';

export interface WizardTrackTarget {
  trackId: string;
  trackName: string;
  /** The content is locked on the track. */
  locked: boolean;
  /** The wizard may write this content to the track. */
  writable: boolean;
  /** Why not, in plain words, when it may not. */
  reason?: string;
}

type TrackLocks = Pick<Track, 'id' | 'name' | 'criteriaLockedAt' | 'rangeSetLockedAt' | 'participationLockedAt'>;

const LOCKED_REASON: Record<WizardTrackContent, string> = {
  criteria: 'Criteria & scales are locked on this track.',
  weights: 'Criteria & scales are locked on this track, and their weights with them.',
  ranges: 'Value ranges are locked on this track.',
  people: 'People are locked on this track.',
};

function trackHoldsContentLocked(content: WizardTrackContent, track: TrackLocks): boolean {
  switch (content) {
    case 'criteria':
    case 'weights':
      // A weight is part of the criteria set (migration 1047): one lock holds both.
      return track.criteriaLockedAt != null;
    case 'ranges':
      // The Criteria & Scales lock covers ranges too; the Value Ranges tab has its own.
      return track.criteriaLockedAt != null || track.rangeSetLockedAt != null;
    case 'people':
      return track.participationLockedAt != null;
  }
}

export function wizardTrackTarget(
  content: WizardTrackContent,
  track: TrackLocks,
  gate?: { allowed: boolean; reason?: string },
): WizardTrackTarget {
  const base = { trackId: track.id, trackName: track.name };
  if (trackHoldsContentLocked(content, track)) {
    return { ...base, locked: true, writable: false, reason: LOCKED_REASON[content] };
  }
  if (gate && !gate.allowed) {
    return { ...base, locked: false, writable: false, reason: gate.reason };
  }
  return { ...base, locked: false, writable: true };
}

export type Coverage = 'all' | 'some' | 'none';

/** On how many of the chosen tracks a key (a criterion, a person) already is. */
export function keyCoverage(
  present: ReadonlyMap<string, ReadonlySet<string>>,
  trackIds: readonly string[],
  key: string,
): Coverage {
  if (trackIds.length === 0) return 'none';
  const on = trackIds.filter((id) => present.get(id)?.has(key)).length;
  return on === 0 ? 'none' : on === trackIds.length ? 'all' : 'some';
}

export interface TrackWrite {
  trackId: string;
  key: string;
}

/**
 * For each key the person decided on: add it to the chosen tracks that lack it
 * (decided true), or take it off the chosen tracks that have it (decided false).
 * A key the person did not touch is not written anywhere.
 */
export function planTrackWrites(
  present: ReadonlyMap<string, ReadonlySet<string>>,
  trackIds: readonly string[],
  decided: ReadonlyMap<string, boolean>,
): { add: TrackWrite[]; remove: TrackWrite[] } {
  const add: TrackWrite[] = [];
  const remove: TrackWrite[] = [];
  for (const [key, want] of decided) {
    for (const trackId of trackIds) {
      const has = present.get(trackId)?.has(key) ?? false;
      if (want && !has) add.push({ trackId, key });
      if (!want && has) remove.push({ trackId, key });
    }
  }
  return { add, remove };
}
